"use client";

import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import { useRouter } from "next/navigation";
import { useDispatch } from "react-redux";
import { useSafeSelector } from "@package/util/hooks/useSafeSelector";
import {
  GetAllCartItem,
  RemoveCartItem,
  UpdateCartItem,
} from "@package/api/slices/product-processing-slices/CartSlice";
import { formatCurrencyAmount } from "@package/util/utils/currency";
import { getCartItemMediaUploads } from "./cartMediaUtils";
import {
  getSelectedCartExtraCharge,
  getSelectedCartLineTotal,
  getSelectedCartStock,
  getSelectedCartUnitPrice,
} from "./cartPricingUtils";

function formatCurrency(value, theme) {
  return formatCurrencyAmount(value, theme);
}

function getVariantLabel(value, fallbacks = []) {
  if (!value) return "";
  if (typeof value === "string") return value;

  for (const key of fallbacks) {
    const resolvedValue = value?.[key];
    if (
      resolvedValue !== undefined &&
      resolvedValue !== null &&
      resolvedValue !== ""
    ) {
      return String(resolvedValue);
    }
  }

  return "";
}

function getStockInfo(cartItem = {}) {
  return getSelectedCartStock(cartItem);
}

function buildProductImageUrl(product = {}) {
  const imagePath =
    product?.selectedImageThumbKey ||
    product?.selectedImageKey ||
    product?.imageKey ||
    product?.thumbnailUrl ||
    product?.imageUrl ||
    "";

  if (!imagePath) return "";
  if (/^https?:\/\//i.test(imagePath)) return imagePath;

  const baseUrl = process.env.NEXT_PUBLIC_SECURED_URL || "";
  if (!baseUrl) return imagePath;

  return `${baseUrl.replace(/\/+$/, "")}/${String(imagePath).replace(/^\/+/, "")}`;
}

function getDrawerTheme(theme = {}) {
  const isEnterprise =
    theme?.variant === "enterprise" || theme?.themeVariant === "enterprise";
  const primary =
    theme?.cartPopup?.primaryColor ||
    (isEnterprise ? "#19497A" : null) ||
    theme?.primaryColor ||
    theme?.accentColor ||
    "#6847ad";
  const hover =
    theme?.cartPopup?.primaryHoverColor ||
    (isEnterprise ? "#0F315A" : null) ||
    theme?.navHoverColor ||
    theme?.secondaryColor ||
    primary;
  const text =
    theme?.cartPopup?.textColor ||
    (isEnterprise ? "#0F2D4A" : null) ||
    theme?.headingColor ||
    theme?.textColor ||
    "#111827";
  const muted =
    theme?.cartPopup?.mutedTextColor ||
    (isEnterprise ? "#496B86" : null) ||
    theme?.bodyTextColor ||
    theme?.subtextColor ||
    "#5f6368";

  return {
    primary,
    hover,
    text,
    muted,
    fontFamily:
      theme?.cartPopup?.fontFamily ||
      theme?.navLinkFontFamily ||
      theme?.fontFamily ||
      "inherit",
    background: theme?.cartPopup?.background || "#ffffff",
    border:
      theme?.cartPopup?.border ||
      (isEnterprise ? "rgba(25, 73, 122, 0.14)" : "rgba(17, 24, 39, 0.12)"),
    softBackground:
      theme?.cartPopup?.softBackground || (isEnterprise ? "#F5F9FC" : "#f5f5f5"),
    secondaryActionBackground:
      theme?.cartPopup?.secondaryActionBackground ||
      (isEnterprise ? "#E7F1F8" : "#ff4964"),
    secondaryActionText:
      theme?.cartPopup?.secondaryActionText || (isEnterprise ? "#0F2D4A" : "#ffffff"),
  };
}

export default function CartPopupDrawer({ open, onClose, theme }) {
  const dispatch = useDispatch();
  const router = useRouter();
  const [activeCartItemId, setActiveCartItemId] = useState(null);
  const [stockWarnings, setStockWarnings] = useState({});
  const [showUnavailableWarnings, setShowUnavailableWarnings] = useState(false);
  const {
    loading = false,
    cartItems = {},
    updateLoading = false,
    removeLoading = false,
  } = useSafeSelector((state) => state.cart, {});
  const drawerTheme = getDrawerTheme(theme);
  const items = Array.isArray(cartItems?.items) ? cartItems.items : [];
  const totalAmount = Number(cartItems?.totalAmount) || 0;
  const isMutating =
    activeCartItemId !== null && (updateLoading || removeLoading);

  useEffect(() => {
    if (!open) return;

    dispatch(GetAllCartItem({}));
  }, [dispatch, open]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open]);

  const refreshCart = () => {
    dispatch(GetAllCartItem({}));
  };

  const updateQuantity = (cartItem, nextQuantity) => {
    const quantity = Number(nextQuantity) || 1;

    setActiveCartItemId(cartItem?.cartItemId);
    dispatch(
      UpdateCartItem({
        cartItemId: cartItem?.cartItemId || 0,
        productId: cartItem?.product?.productId || 0,
        quantity,
        videoImageMapDTOS: getCartItemMediaUploads(cartItem),
      }),
    ).then((res) => {
      setActiveCartItemId(null);
      if (UpdateCartItem.fulfilled.match(res)) {
        setStockWarnings((prev) => ({
          ...prev,
          [cartItem?.cartItemId]: "",
        }));
        refreshCart();
      }
    });
  };

  const incrementQuantity = (cartItem) => {
    const quantity = Number(cartItem?.quantity) || 1;
    const { hasStockLimit, stock } = getStockInfo(cartItem);

    if (hasStockLimit && quantity >= stock) {
      setStockWarnings((prev) => ({
        ...prev,
        [cartItem?.cartItemId]: stock > 0
          ? `Stock limit reached. Only ${stock} left.`
          : "Out of stock",
      }));
      return;
    }

    updateQuantity(cartItem, quantity + 1);
  };

  const decrementQuantity = (cartItem) => {
    const quantity = Number(cartItem?.quantity) || 1;

    if (quantity <= 1) return;
    updateQuantity(cartItem, quantity - 1);
  };

  const removeItem = (cartItem) => {
    setActiveCartItemId(cartItem?.cartItemId);
    dispatch(RemoveCartItem({ cartItemId: cartItem?.cartItemId || 0 })).then(
      (res) => {
        setActiveCartItemId(null);
        if (RemoveCartItem.fulfilled.match(res)) {
          refreshCart();
        }
      },
    );
  };

  const navigateTo = (path) => {
    router.push(path);
    onClose();
  };

  const handleCheckout = () => {
    const hasUnavailableProduct = items.some(
      (cartItem) => cartItem?.productNotAvailable === true,
    );

    if (hasUnavailableProduct) {
      setShowUnavailableWarnings(true);
      return;
    }

    navigateTo("/checkout");
  };

  if (!open) return null;

  return (
    <div
      className="cart-popup-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cart-popup-title"
      style={{
        "--cart-popup-primary": drawerTheme.primary,
        "--cart-popup-primary-hover": drawerTheme.hover,
        "--cart-popup-text": drawerTheme.text,
        "--cart-popup-muted": drawerTheme.muted,
        "--cart-popup-bg": drawerTheme.background,
        "--cart-popup-border": drawerTheme.border,
        "--cart-popup-soft-bg": drawerTheme.softBackground,
        "--cart-popup-secondary-bg": drawerTheme.secondaryActionBackground,
        "--cart-popup-secondary-text": drawerTheme.secondaryActionText,
        "--cart-popup-font": drawerTheme.fontFamily,
      }}
    >
      <button
        type="button"
        className="cart-popup-overlay"
        aria-label="Close cart popup"
        onClick={onClose}
      />

      <aside className="cart-popup-panel">
        <header className="cart-popup-header">
          <h2 id="cart-popup-title">Your cart</h2>
          <button
            type="button"
            className="cart-popup-icon-button"
            aria-label="Close cart popup"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </button>
        </header>

        <div className="cart-popup-content">
          {loading ? (
            <div className="cart-popup-loading" aria-live="polite">
              <span className="cart-popup-spinner" />
              <span>Loading cart...</span>
            </div>
          ) : null}

          {!loading && items.length === 0 ? (
            <div className="cart-popup-empty">
              <ShoppingBagOutlinedIcon />
              <h3>No product added</h3>
              <p>Please add product to cart and continue to place order.</p>
            </div>
          ) : null}

          {!loading && items.length > 0 ? (
            <div className="cart-popup-items">
              {items.map((cartItem) => {
                const product = cartItem?.product || {};
                const quantity = Number(cartItem?.quantity) || 1;
                const unitPrice = getSelectedCartUnitPrice(cartItem);
                const lineTotal = getSelectedCartLineTotal(cartItem);
                const extraChargeAmount = getSelectedCartExtraCharge(cartItem);
                const imageUrl = buildProductImageUrl(product);
                const sizeLabel = getVariantLabel(cartItem?.size, [
                  "name",
                  "size",
                  "label",
                  "value",
                  "title",
                ]);
                const colorLabel = getVariantLabel(cartItem?.color, [
                  "colorName",
                  "colorCode",
                  "label",
                  "value",
                  "title",
                ]);
                const designLabel = getVariantLabel(cartItem?.design, [
                  "design",
                  "name",
                  "label",
                  "value",
                  "title",
                ]);
                const { hasStockLimit, stock } = getStockInfo(cartItem);
                const isProductUnavailable =
                  cartItem?.productNotAvailable === true;
                const isOutOfStock =
                  isProductUnavailable || (hasStockLimit && stock <= 0);
                const isAtStockLimit =
                  !isProductUnavailable && hasStockLimit && quantity >= stock;
                const unavailableWarning =
                  showUnavailableWarnings && isProductUnavailable
                    ? "This product is currently not available. Please remove it from your cart before continuing."
                    : "";
                const isCurrentItemMutating =
                  isMutating && activeCartItemId === cartItem?.cartItemId;

                return (
                  <article
                    className="cart-popup-item"
                    key={cartItem?.cartItemId || product?.productId}
                  >
                    <div className="cart-popup-thumb">
                      {imageUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={imageUrl} alt={product?.name || "Product"} />
                      ) : (
                        <ShoppingBagOutlinedIcon />
                      )}
                    </div>

                    <div className="cart-popup-item-main">
                      <div className="cart-popup-item-top">
                        <div>
                          <h3 title={product?.name || ""}>
                            {product?.name || "Product"}
                          </h3>
                          {(sizeLabel || colorLabel || designLabel) && (
                            <p className="cart-popup-variant">
                              {[designLabel, sizeLabel, colorLabel]
                                .filter(Boolean)
                                .join(" / ")}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          className="cart-popup-remove"
                          aria-label={`Remove ${product?.name || "product"}`}
                          disabled={isCurrentItemMutating}
                          onClick={() => removeItem(cartItem)}
                        >
                          {isCurrentItemMutating && removeLoading ? (
                            <span className="cart-popup-small-spinner" />
                          ) : (
                            <DeleteOutlineIcon fontSize="small" />
                          )}
                        </button>
                      </div>

                      <div className="cart-popup-item-bottom">
                        <div className="cart-popup-qty">
                          <button
                            type="button"
                            aria-label="Decrease quantity"
                            disabled={isCurrentItemMutating || quantity <= 1}
                            onClick={() => decrementQuantity(cartItem)}
                          >
                            <RemoveIcon fontSize="small" />
                          </button>
                          <span>{quantity}</span>
                          <button
                            type="button"
                            aria-label="Increase quantity"
                            disabled={
                              isCurrentItemMutating ||
                              isOutOfStock ||
                              isAtStockLimit
                            }
                            onClick={() => incrementQuantity(cartItem)}
                          >
                            <AddIcon fontSize="small" />
                          </button>
                        </div>

                        <strong>{formatCurrency(lineTotal, theme)}</strong>
                      </div>

                      {extraChargeAmount > 0 ? (
                        <p className="cart-popup-extra-charge">
                          <span>
                            {cartItem?.extraChargeDescription ||
                              "Additional charge"}
                          </span>
                          <strong>
                            +{formatCurrency(extraChargeAmount, theme)}
                          </strong>
                        </p>
                      ) : null}

                      {unavailableWarning ||
                      stockWarnings?.[cartItem?.cartItemId] ? (
                        <p className="cart-popup-warning">
                          {unavailableWarning ||
                            stockWarnings[cartItem.cartItemId]}
                        </p>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : null}
        </div>

        <footer className="cart-popup-footer">
          <div className="cart-popup-total">
            <span>Total</span>
            <strong>{formatCurrency(totalAmount, theme)}</strong>
          </div>
          <p>Tax included. Shipping calculated at checkout.</p>
          <button
            type="button"
            className="cart-popup-checkout"
            disabled={!items.length}
            onClick={handleCheckout}
          >
            <ShoppingCartCheckoutIcon fontSize="small" />
            <span>Checkout</span>
          </button>
          <button
            type="button"
            className="cart-popup-view-cart"
            onClick={() => navigateTo("/cart")}
          >
            View Cart
          </button>
        </footer>
      </aside>

      <style jsx>{`
        .cart-popup-root {
          position: fixed;
          inset: 0;
          z-index: 2500;
          font-family: var(--cart-popup-font);
          color: var(--cart-popup-text);
        }

        .cart-popup-overlay {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: 0;
          background: rgba(0, 0, 0, 0.48);
          cursor: pointer;
        }

        .cart-popup-panel {
          position: absolute;
          top: 0;
          right: 0;
          width: min(620px, calc(100vw - 24px));
          height: 100%;
          background: var(--cart-popup-bg);
          box-shadow: -18px 0 44px rgba(15, 23, 42, 0.22);
          display: grid;
          grid-template-rows: auto minmax(0, 1fr) auto;
          animation: cartPopupSlideIn 180ms ease-out;
        }

        .cart-popup-header {
          min-height: 82px;
          padding: 24px 38px 22px;
          border-bottom: 1px solid var(--cart-popup-border);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }

        .cart-popup-header h2 {
          margin: 0;
          font-size: 1.35rem;
          line-height: 1.2;
          font-weight: 700;
          color: var(--cart-popup-text);
        }

        .cart-popup-icon-button,
        .cart-popup-remove,
        .cart-popup-qty button {
          border: 0;
          color: var(--cart-popup-text);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .cart-popup-icon-button {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          background: var(--cart-popup-soft-bg);
        }

        .cart-popup-content {
          overflow: auto;
          padding: 26px 38px;
        }

        .cart-popup-loading,
        .cart-popup-empty {
          min-height: 260px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 10px;
          color: var(--cart-popup-muted);
        }

        .cart-popup-empty svg {
          width: 46px;
          height: 46px;
          color: var(--cart-popup-primary);
        }

        .cart-popup-empty h3,
        .cart-popup-empty p {
          margin: 0;
        }

        .cart-popup-empty h3 {
          font-size: 1.05rem;
          color: var(--cart-popup-text);
        }

        .cart-popup-empty p {
          max-width: 310px;
          font-size: 0.92rem;
          line-height: 1.45;
        }

        .cart-popup-items {
          display: grid;
          gap: 0;
        }

        .cart-popup-item {
          display: grid;
          grid-template-columns: 106px minmax(0, 1fr);
          gap: 22px;
          padding: 22px 0;
          border-bottom: 1px solid var(--cart-popup-border);
        }

        .cart-popup-item:first-child {
          padding-top: 0;
        }

        .cart-popup-thumb {
          width: 106px;
          aspect-ratio: 1;
          border-radius: 8px;
          overflow: hidden;
          background: var(--cart-popup-soft-bg);
          border: 1px solid var(--cart-popup-border);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--cart-popup-muted);
        }

        .cart-popup-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .cart-popup-item-main {
          min-width: 0;
          display: grid;
          gap: 14px;
        }

        .cart-popup-item-top {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: start;
          gap: 14px;
        }

        .cart-popup-item-top h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 750;
          line-height: 1.25;
          color: var(--cart-popup-text);
          overflow-wrap: anywhere;
        }

        .cart-popup-variant,
        .cart-popup-warning {
          margin: 6px 0 0;
          font-size: 0.86rem;
          line-height: 1.35;
        }

        .cart-popup-variant {
          color: var(--cart-popup-muted);
        }

        .cart-popup-warning {
          color: #c2410c;
          font-weight: 650;
        }

        .cart-popup-extra-charge {
          margin: -4px 0 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          color: var(--cart-popup-muted);
          font-size: 0.84rem;
          line-height: 1.35;
        }

        .cart-popup-extra-charge strong {
          color: var(--cart-popup-text);
          white-space: nowrap;
        }

        .cart-popup-remove {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          background: transparent;
        }

        .cart-popup-remove:hover,
        .cart-popup-icon-button:hover {
          background: rgba(0, 0, 0, 0.08);
        }

        .cart-popup-item-bottom {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }

        .cart-popup-item-bottom strong {
          font-size: 0.95rem;
          color: var(--cart-popup-text);
          white-space: nowrap;
        }

        .cart-popup-qty {
          height: 44px;
          min-width: 126px;
          border: 1px solid var(--cart-popup-border);
          border-radius: 7px;
          display: grid;
          grid-template-columns: 38px 1fr 38px;
          align-items: center;
          overflow: hidden;
          background: #ffffff;
        }

        .cart-popup-qty button {
          height: 100%;
          background: transparent;
        }

        .cart-popup-qty button:disabled,
        .cart-popup-remove:disabled,
        .cart-popup-checkout:disabled {
          cursor: not-allowed;
          opacity: 0.45;
        }

        .cart-popup-qty span {
          text-align: center;
          font-weight: 700;
          font-size: 0.95rem;
        }

        .cart-popup-footer {
          padding: 18px 38px 24px;
          border-top: 1px solid var(--cart-popup-border);
          box-shadow: 0 -8px 28px rgba(15, 23, 42, 0.07);
          background: var(--cart-popup-bg);
        }

        .cart-popup-total {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 10px;
          font-size: 1.36rem;
        }

        .cart-popup-total strong {
          font-size: 1.45rem;
          color: var(--cart-popup-text);
          white-space: nowrap;
        }

        .cart-popup-footer p {
          margin: 0 0 16px;
          color: var(--cart-popup-muted);
          font-size: 0.92rem;
          line-height: 1.45;
        }

        .cart-popup-checkout,
        .cart-popup-view-cart {
          width: 100%;
          min-height: 52px;
          border: 0;
          border-radius: 7px;
          font: inherit;
          font-weight: 750;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .cart-popup-checkout {
          background: var(--cart-popup-primary);
          color: #ffffff;
          margin-bottom: 12px;
        }

        .cart-popup-checkout:hover {
          background: var(--cart-popup-primary-hover);
        }

        .cart-popup-view-cart {
          background: var(--cart-popup-secondary-bg);
          color: var(--cart-popup-secondary-text);
        }

        .cart-popup-spinner,
        .cart-popup-small-spinner {
          border-radius: 50%;
          border: 2px solid rgba(0, 0, 0, 0.18);
          border-top-color: var(--cart-popup-primary);
          display: inline-block;
          animation: cartPopupSpin 800ms linear infinite;
        }

        .cart-popup-spinner {
          width: 26px;
          height: 26px;
        }

        .cart-popup-small-spinner {
          width: 16px;
          height: 16px;
        }

        @keyframes cartPopupSpin {
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes cartPopupSlideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }

        @media (max-width: 640px) {
          .cart-popup-panel {
            top: auto;
            bottom: 0;
            right: 0;
            left: 0;
            width: 100%;
            height: min(88vh, 720px);
            border-radius: 18px 18px 0 0;
            animation: cartPopupSlideUp 180ms ease-out;
          }

          .cart-popup-header,
          .cart-popup-content,
          .cart-popup-footer {
            padding-left: 20px;
            padding-right: 20px;
          }

          .cart-popup-header {
            min-height: 70px;
            padding-top: 18px;
            padding-bottom: 16px;
          }

          .cart-popup-item {
            grid-template-columns: 82px minmax(0, 1fr);
            gap: 14px;
            padding: 18px 0;
          }

          .cart-popup-thumb {
            width: 82px;
          }

          .cart-popup-item-bottom {
            align-items: flex-start;
            flex-direction: column;
            gap: 12px;
          }

          .cart-popup-total strong {
            font-size: 1.25rem;
          }
        }

        @keyframes cartPopupSlideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
